import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Calculator, Wind, MoreHorizontal, Eraser, Check, X, ArrowRightLeft, GraduationCap, Plus, Trash2, Scale } from 'lucide-react';
import { persianNumber } from '../services/utils';
import { Task, Note } from '../types';

interface ToolsProps {
  tasks: Task[];
  notes: Note[];
  deadline: string | null;
}

const Tools: React.FC<ToolsProps> = ({ tasks, notes, deadline }) => {
  const [activeMobileTool, setActiveMobileTool] = useState<string | null>(null);

  // --- 1. Simple Calculator Component ---
  const SimpleCalculator = () => {
    const [display, setDisplay] = useState('0');
    const [equation, setEquation] = useState('');

    const handleBtn = (val: string) => {
      if (val === 'C') {
        setDisplay('0');
        setEquation('');
      } else if (val === '=') {
        try {
          // eslint-disable-next-line no-eval
          const res = eval(equation.replace(/×/g, '*').replace(/÷/g, '/'));
          setDisplay(String(res));
          setEquation(String(res));
        } catch {
          setDisplay('خطا');
          setEquation('');
        }
      } else {
        const newEq = equation === '0' ? val : equation + val;
        setEquation(newEq);
        setDisplay(newEq);
      }
    };

    const buttons = [
      'C', '÷', '×', '⌫',
      '7', '8', '9', '-',
      '4', '5', '6', '+',
      '1', '2', '3', '=',
      '0', '.'
    ];

    return (
      <div className="bg-slate-50 dark:bg-slate-800 p-4 lg:rounded-xl flex flex-col gap-2 h-full rounded-none">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-lg text-right font-mono text-xl md:text-2xl h-24 lg:h-16 flex items-center justify-end overflow-hidden border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 shadow-inner" dir="ltr">
           {display}
        </div>
        <div className="grid grid-cols-4 gap-2 flex-1">
          {buttons.map((btn, i) => (
            <button
              key={i}
              onClick={() => handleBtn(btn === '⌫' ? '' : btn)}
              className={`
                rounded-lg font-bold transition-all active:scale-95 text-xl lg:text-base
                ${btn === '=' ? 'col-span-1 bg-primary-600 text-white hover:bg-primary-700 shadow-md shadow-primary-200 dark:shadow-none' : 
                  ['C', '÷', '×', '-', '+', '⌫'].includes(btn) ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/60' : 
                  'bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 shadow-sm'}
                ${btn === '0' ? 'col-span-2' : ''}
              `}
            >
              {btn}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // --- 2. Breathing Exercise Component ---
  const BreathingTool = () => {
    const [step, setStep] = useState(0); // 0: Idle, 1: Inhale, 2: Hold, 3: Exhale
    
    const startBreathing = () => {
       setStep(1);
       // Simple mock cycle
       setTimeout(() => setStep(2), 4000); // Inhale 4s
       setTimeout(() => setStep(3), 8000); // Hold 4s
       setTimeout(() => setStep(0), 12000); // Exhale 4s -> Stop
    };

    return (
      <div className="h-full flex flex-col items-center justify-center gap-8 py-6 text-center bg-white dark:bg-slate-900 lg:bg-transparent lg:dark:bg-transparent">
        <div className={`
           w-48 h-48 lg:w-32 lg:h-32 rounded-full flex items-center justify-center transition-all duration-[4000ms]
           ${step === 0 ? 'bg-slate-200 dark:bg-slate-700 scale-100' : ''}
           ${step === 1 ? 'bg-blue-400 scale-125 shadow-[0_0_60px_rgba(96,165,250,0.6)]' : ''}
           ${step === 2 ? 'bg-indigo-500 scale-125 shadow-[0_0_60px_rgba(99,102,241,0.6)]' : ''}
           ${step === 3 ? 'bg-green-400 scale-100' : ''}
        `}>
           <Wind size={step === 0 ? 40 : 60} className={`text-white transition-opacity ${step !== 0 ? 'animate-pulse' : ''}`} />
        </div>
        <div>
           <h3 className="text-2xl lg:text-xl font-bold mb-2 text-slate-800 dark:text-slate-100">
             {step === 0 && 'تمرین تنفس'}
             {step === 1 && 'دم (نفس بگیرید)'}
             {step === 2 && 'حبس کنید'}
             {step === 3 && 'بازدم (رها کنید)'}
           </h3>
           <p className="text-base lg:text-sm text-slate-500 dark:text-slate-400">
             {step === 0 ? 'برای کاهش استرس کلیک کنید' : 'آرام باشید...'}
           </p>
        </div>
        {step === 0 && (
           <button onClick={startBreathing} className="px-8 py-3 lg:px-6 lg:py-2 bg-slate-800 dark:bg-slate-700 text-white rounded-full hover:bg-slate-700 transition-colors text-lg lg:text-base shadow-lg">شروع</button>
        )}
      </div>
    );
  };

  // --- 3. Unit Converter Component ---
  const UnitConverter = () => {
    type Category = 'LENGTH' | 'MASS' | 'VOLUME';
    const [category, setCategory] = useState<Category>('LENGTH');
    const [amount, setAmount] = useState<string>('1');
    const [fromUnit, setFromUnit] = useState<string>('m');
    const [toUnit, setToUnit] = useState<string>('cm');

    const categories: Record<Category, { label: string, units: Record<string, number>, unitLabels: Record<string, string> }> = {
      LENGTH: { 
        label: 'طول', 
        units: { m: 1, km: 1000, cm: 0.01, mm: 0.001, inch: 0.0254, ft: 0.3048, mile: 1609.34 },
        unitLabels: { m: 'متر', km: 'کیلومتر', cm: 'سانتیمتر', mm: 'میلیمتر', inch: 'اینچ', ft: 'فوت', mile: 'مایل' }
      },
      MASS: { 
        label: 'جرم', 
        units: { kg: 1, g: 0.001, mg: 0.000001, lb: 0.453592, oz: 0.0283495 },
        unitLabels: { kg: 'کیلوگرم', g: 'گرم', mg: 'میلی‌گرم', lb: 'پوند', oz: 'اونس' }
      },
      VOLUME: {
        label: 'حجم',
        units: { l: 1, ml: 0.001, gal: 3.78541 },
        unitLabels: { l: 'لیتر', ml: 'میلی‌لیتر', gal: 'گالن' }
      }
    };

    const convert = () => {
      const val = parseFloat(amount);
      if (isNaN(val)) return '---';
      
      const cat = categories[category];
      const baseValue = val * cat.units[fromUnit];
      const result = baseValue / cat.units[toUnit];
      
      return Number(result.toPrecision(6)).toString();
    };

    const handleCategoryChange = (c: Category) => {
        setCategory(c);
        const keys = Object.keys(categories[c].units);
        setFromUnit(keys[0]);
        setToUnit(keys[1] || keys[0]);
    };

    return (
        <div className="h-full flex flex-col p-4 lg:p-2 gap-6 lg:gap-4 bg-white dark:bg-slate-900 lg:bg-transparent lg:dark:bg-transparent">
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1.5">
                {(Object.keys(categories) as Category[]).map(c => (
                    <button
                        key={c}
                        onClick={() => handleCategoryChange(c)}
                        className={`flex-1 py-2 lg:py-1.5 text-sm lg:text-xs font-bold rounded-lg transition-all ${category === c ? 'bg-white dark:bg-slate-600 shadow text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400'}`}
                    >
                        {categories[c].label}
                    </button>
                ))}
            </div>

            <div className="flex-1 flex flex-col justify-center gap-6 lg:gap-4">
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-3">
                    <div className="space-y-2">
                        <label className="text-sm lg:text-xs text-slate-400">مقدار</label>
                        <input 
                            type="number" 
                            value={amount} 
                            onChange={e => setAmount(e.target.value)}
                            className="w-full p-4 lg:p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-left text-xl lg:text-base text-slate-800 dark:text-white dir-ltr focus:border-primary-500 focus:outline-none"
                        />
                    </div>
                    <div className="space-y-2">
                         <label className="text-sm lg:text-xs text-slate-400">تبدیل به</label>
                         <div className="h-[60px] lg:h-[42px] flex items-center px-4 lg:px-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-xl text-primary-700 dark:text-primary-300 font-bold text-xl lg:text-base dir-ltr overflow-hidden">
                             {convert()}
                         </div>
                    </div>
                 </div>

                 <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-2">
                    <select 
                        value={fromUnit} 
                        onChange={e => setFromUnit(e.target.value)}
                        className="w-full lg:flex-1 p-3 lg:p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-base lg:text-sm text-slate-700 dark:text-slate-200"
                    >
                        {Object.keys(categories[category].units).map(u => <option key={u} value={u}>{categories[category].unitLabels[u]}</option>)}
                    </select>
                    <ArrowRightLeft size={20} className="text-slate-400 rotate-90 lg:rotate-0" />
                    <select 
                        value={toUnit} 
                        onChange={e => setToUnit(e.target.value)}
                        className="w-full lg:flex-1 p-3 lg:p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-base lg:text-sm text-slate-700 dark:text-slate-200"
                    >
                        {Object.keys(categories[category].units).map(u => <option key={u} value={u}>{categories[category].unitLabels[u]}</option>)}
                    </select>
                 </div>
            </div>
        </div>
    );
  };

  // --- 4. Grade Calculator Component ---
  const GradeCalculator = () => {
     const [rows, setRows] = useState([{ id: 1, grade: '', credit: '' }]);

     const addRow = () => {
         setRows([...rows, { id: Date.now(), grade: '', credit: '' }]);
     };

     const removeRow = (id: number) => {
         if (rows.length > 1) {
             setRows(rows.filter(r => r.id !== id));
         } else {
             setRows([{ id: Date.now(), grade: '', credit: '' }]);
         }
     };

     const updateRow = (id: number, field: 'grade' | 'credit', value: string) => {
         setRows(rows.map(r => r.id === id ? { ...r, [field]: value } : r));
     };

     const calculateAverage = () => {
         let totalPoints = 0;
         let totalCredits = 0;
         
         rows.forEach(r => {
             const g = parseFloat(r.grade);
             const c = parseFloat(r.credit);
             if (!isNaN(g) && !isNaN(c)) {
                 totalPoints += g * c;
                 totalCredits += c;
             }
         });

         if (totalCredits === 0) return 0;
         return (totalPoints / totalCredits).toFixed(2);
     };

     const average = calculateAverage();

     return (
         <div className="flex flex-col h-full bg-white dark:bg-slate-900 lg:bg-transparent lg:dark:bg-transparent p-4 lg:p-0">
             <div className="flex-1 overflow-y-auto max-h-[calc(100dvh-200px)] lg:max-h-[250px] custom-scrollbar mb-4 pr-1">
                 <div className="flex text-sm lg:text-xs text-slate-400 mb-2 px-1">
                     <span className="flex-1">نمره (۰-۲۰)</span>
                     <span className="w-24 lg:w-20 text-center">واحد</span>
                     <span className="w-8"></span>
                 </div>
                 <div className="space-y-3 lg:space-y-2">
                     {rows.map((row) => (
                         <div key={row.id} className="flex gap-3 lg:gap-2 items-center">
                             <input 
                                type="number" 
                                placeholder="نمره"
                                value={row.grade}
                                onChange={(e) => updateRow(row.id, 'grade', e.target.value)}
                                className="flex-1 p-3 lg:p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-base lg:text-sm text-center focus:border-primary-500 outline-none text-slate-800 dark:text-white"
                             />
                             <input 
                                type="number" 
                                placeholder="1"
                                value={row.credit}
                                onChange={(e) => updateRow(row.id, 'credit', e.target.value)}
                                className="w-24 lg:w-20 p-3 lg:p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-base lg:text-sm text-center focus:border-primary-500 outline-none text-slate-800 dark:text-white"
                             />
                             <button onClick={() => removeRow(row.id)} className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-red-500 transition-colors">
                                 <Trash2 size={20} />
                             </button>
                         </div>
                     ))}
                 </div>
             </div>
             
             <div className="border-t border-slate-100 dark:border-slate-800 pt-4 lg:pt-3 mt-auto">
                 <div className="flex justify-between items-center mb-3">
                     <button onClick={addRow} className="text-sm lg:text-xs flex items-center gap-1 text-primary-600 dark:text-primary-400 font-bold hover:bg-primary-50 dark:hover:bg-primary-900/20 px-3 py-2 lg:px-2 lg:py-1 rounded-lg transition-colors">
                         <Plus size={18} /> افزودن درس
                     </button>
                     <div className="text-right">
                         <span className="text-sm lg:text-xs text-slate-500 dark:text-slate-400 ml-2">معدل کل:</span>
                         <span className={`text-2xl lg:text-xl font-bold ${Number(average) >= 12 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                             {persianNumber(average)}
                         </span>
                     </div>
                 </div>
             </div>
         </div>
     );
  };

  return (
    <div className="space-y-6">
      
      {/* --- DESKTOP VIEW: Widget Grid --- */}
      <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-6">
         {/* 1. Calculator Widget */}
         <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 h-[400px]">
             <h3 className="font-bold text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
               <Calculator size={18} /> ماشین حساب
             </h3>
             <SimpleCalculator />
         </div>

         {/* 2. Unit Converter Widget */}
         <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 h-[400px]">
             <h3 className="font-bold text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
               <ArrowRightLeft size={18} /> تبدیل واحد
             </h3>
             <UnitConverter />
         </div>

         {/* 3. GPA Calculator Widget */}
         <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 h-[400px]">
             <h3 className="font-bold text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
               <GraduationCap size={18} /> محاسبه معدل
             </h3>
             <GradeCalculator />
         </div>

         {/* 4. Breathing Widget */}
         <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 h-[400px]">
             <h3 className="font-bold text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
               <Wind size={18} /> آرامش ذهن
             </h3>
             <BreathingTool />
         </div>
      </div>

      {/* --- MOBILE VIEW: App Drawer --- */}
      <div className="lg:hidden">
         <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            
            {/* Slot for Pomodoro (Mounted via Portal from Pomodoro.tsx) */}
            <div id="mobile-tools-pomodoro-slot" className="col-span-2 min-h-[220px]">
               {/* Content injected by Pomodoro component */}
            </div>

            {/* Calculator App Icon */}
            <button 
               onClick={() => setActiveMobileTool('CALC')}
               className="aspect-square bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform"
            >
               <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center text-orange-600 dark:text-orange-400">
                  <Calculator size={24} />
               </div>
               <span className="font-medium text-sm text-slate-700 dark:text-slate-200">ماشین حساب</span>
            </button>

            {/* Unit Converter App Icon */}
            <button 
               onClick={() => setActiveMobileTool('UNIT')}
               className="aspect-square bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform"
            >
               <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <ArrowRightLeft size={24} />
               </div>
               <span className="font-medium text-sm text-slate-700 dark:text-slate-200">تبدیل واحد</span>
            </button>

            {/* GPA Calculator App Icon */}
            <button 
               onClick={() => setActiveMobileTool('GPA')}
               className="aspect-square bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform"
            >
               <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-xl flex items-center justify-center text-violet-600 dark:text-violet-400">
                  <GraduationCap size={24} />
               </div>
               <span className="font-medium text-sm text-slate-700 dark:text-slate-200">محاسبه معدل</span>
            </button>

            {/* Breathing App Icon */}
            <button 
               onClick={() => setActiveMobileTool('BREATHE')}
               className="aspect-square bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform"
            >
               <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                  <Wind size={24} />
               </div>
               <span className="font-medium text-sm text-slate-700 dark:text-slate-200">تنفس</span>
            </button>
         </div>
      </div>

      {/* Mobile Tool Modal Overlay */}
      {activeMobileTool && createPortal(
        <div className="lg:hidden fixed inset-0 z-[100] h-[100dvh] w-screen bg-slate-100 dark:bg-slate-950 flex flex-col animate-in slide-in-from-bottom duration-300">
           <div className="p-3 flex items-center justify-between bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
              <h3 className="font-bold text-lg text-slate-800 dark:text-white">
                {activeMobileTool === 'CALC' && 'ماشین حساب'}
                {activeMobileTool === 'BREATHE' && 'تمرین تنفس'}
                {activeMobileTool === 'UNIT' && 'تبدیل واحد'}
                {activeMobileTool === 'GPA' && 'محاسبه معدل'}
              </h3>
              <button onClick={() => setActiveMobileTool(null)} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-300">
                <X size={20} />
              </button>
           </div>
           {/* Removed padding to allow apps to be full screen */}
           <div className="flex-1 overflow-y-auto">
              {activeMobileTool === 'CALC' && <SimpleCalculator />}
              {activeMobileTool === 'BREATHE' && <BreathingTool />}
              {activeMobileTool === 'UNIT' && <UnitConverter />}
              {activeMobileTool === 'GPA' && <GradeCalculator />}
           </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default Tools;